import Chart from 'chart.js/auto';
import { chartColors } from './chartColors.js';
import { downloadChartCSV } from './chartHelpers.js';

const questionDataCache = {};

async function fetchQuestionData(questionId, groupId) {
    const queryParams = new URLSearchParams({
        question_id: questionId,
    });

    if (groupId) {
        queryParams.append('group_ids', groupId);
    }

    const queryParamsString = queryParams.toString();

    if (questionDataCache[queryParamsString]) {
        return questionDataCache[queryParamsString];
    }

    let url = `${benchmarktool.api_url}/api/answers?${queryParamsString}`;

    try {
        const response = await fetch(url);

        if (!response.ok) {
            throw new Error(`API request failed with status ${response.status}`);
        }

        const data = await response.json();
        questionDataCache[queryParamsString] = data;
        return data;
    } catch (error) {
        console.error('Error fetching question data:', error);
        return {};
    }
}

function hexToRgb(hex) {
    hex = hex.replace(/^#/, '');

    if (hex.length === 3) {
        hex = hex
            .split('')
            .map(char => char + char)
            .join('');
    }

    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return `${r}, ${g}, ${b}`;
}

function generateChartData(label, data, backgroundColor) {
    return {
        label: label,
        data: data,
        backgroundColor: backgroundColor,
        hoverBackgroundColor: `rgba(${hexToRgb(backgroundColor)}, 0.5)`,
    };
}

class DefaultChartComponent extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    static get observedAttributes() {
        return ['question_id', 'group_id'];
    }

    connectedCallback() {
        this.render();
        this.observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        this.buildChart();
                        this.observer.disconnect();
                    }
                });
            },
            { root: null, threshold: 0.1 },
        );

        this.observer.observe(this);
    }

    initChartSource = data => {
        const sourceLabelContainer = document.querySelector(
            '[data-source-label-question-id="' + this.questionId + '"]',
        );
        if (!sourceLabelContainer) {
            return;
        }

        sourceLabelContainer.textContent = data.info ? data.info : '';
    };

    initChartDownloadCsv = () => {
        const downloadButton = document.querySelector('[data-download-button-question-id="' + this.questionId + '"]');

        if (!downloadButton) {
            return;
        }

        downloadButton.addEventListener('click', event => {
            event.preventDefault();

            const chartTitle = downloadButton.getAttribute('data-chart-title') || 'Chart';
            downloadChartCSV({
                chart: this.chart,
                shadowRoot: this.shadowRoot,
                filename: chartTitle,
            });
        });
    };

    buildChart() {
        const questionId = this.getAttribute('question_id');
        const groupId = this.getAttribute('group_id');

        this.questionId = questionId;

        fetchQuestionData(questionId, groupId)
            .then(res => {
                if (!res || (Array.isArray(res) && res.length === 0) || Object.keys(res).length === 0) {
                    this.showErrorMessage('Geen gegevens beschikbaar voor deze grafiek');
                    return;
                }
                this.renderChart(res, questionId, groupId);
            })
            .catch(error => {
                console.error('Error building chart:', error);
                this.showErrorMessage('Fout bij het laden van grafiekgegevens');
            });
    }

    showErrorMessage(message) {
        const container = this.shadowRoot.querySelector('#shortcode-chart-container');
        if (container) {
            container.innerHTML = `<div class="chart-error">${message}</div>`;
        }
    }

    getAnswersFromData(data) {
        if (!data) {
            return [];
        }

        if (Array.isArray(data)) {
            return data.map(answer => ({
                label: answer.answer_label || answer.label || 'Onbekend',
                value: answer.answer_value || answer.value || 0,
            }));
        }

        if (typeof data === 'object' && !Array.isArray(data)) {
            const firstKey = Object.keys(data)[0];
            if (firstKey && Array.isArray(data[firstKey])) {
                return data[firstKey].map(answer => ({
                    label: answer.answer_label || answer.label || 'Onbekend',
                    value: answer.answer_value || answer.value || 0,
                }));
            }
        }

        return [];
    }

    renderChart(data, questionId, groupId) {
        const chartType = 'doughnut';
        const chartDataObj = this.getAnswersFromData(data);

        if (!chartDataObj || chartDataObj.length === 0) {
            this.showErrorMessage('Geen grafiekgegevens beschikbaar');
            return;
        }

        const shortcodeContainer = this.shadowRoot.querySelector('#shortcode-chart-container');

        shortcodeContainer.innerHTML = '';
        const canvas = document.createElement('canvas');
        canvas.classList.add('exp-card-chart__chart');
        canvas.classList.add('js-card-chart-canvas');
        canvas.id = 'js-chart-id-' + questionId;

        const borderWidthValue = 0;
        const hoverOffsetValue = 0;

        const originalChartOptions = {
            indexAxis: 'y',
            tension: 0.4,
            pointRadius: 0,
            aspectRatio: 2,
            responsive: true,
            maintainAspectRatio: false,
            borderWidth: borderWidthValue,
            hoverOffset: hoverOffsetValue,
            interaction: {
                intersect: chartType !== 'line',
                mode: 'index',
            },
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        color: '#595959',
                        font: {
                            family: 'PP Object Sans',
                            size: 11,
                            weight: 'normal',
                            lineHeight: 2,
                        },
                        usePointStyle: true,
                        pointStyle: 'rect',
                    },
                },
                tooltip: {
                    padding: 12,
                    borderWidth: 1,
                    cornerRadius: 8,
                    borderColor: '#e6edff',
                    backgroundColor: '#fff',
                    titleColor: '#333',
                    titleFont: {
                        family: 'PP Object Sans',
                        size: 10,
                        weight: 'bold',
                    },
                    bodyColor: '#595959',
                    bodyFont: {
                        family: 'PP Object Sans',
                        size: 10,
                        weight: 'normal',
                    },
                    usePointStyle: true,
                    pointStyle: 'rect',
                },
            },
        };

        let newChartOptions = {};
        if (chartType !== 'doughnut') {
            newChartOptions = {};
        }

        const finalChartOptions = {
            ...originalChartOptions,
            ...newChartOptions,
        };

        shortcodeContainer.appendChild(canvas);

        const chartData = chartDataObj.map((answer, i) => {
            return generateChartData(answer.label, [answer.value], chartColors[i % chartColors.length]);
        });

        this.chart = new Chart(canvas.getContext('2d'), {
            type: chartType,
            data: {
                labels: chartData.map(chartData => chartData.label),
                datasets: [
                    {
                        label: 'Totaal',
                        data: chartData.map(chartData => chartData.data[0]),
                        backgroundColor: chartData.map(chartData => chartData.backgroundColor),
                        hoverBackgroundColor: chartData.map(chartData => chartData.hoverBackgroundColor),
                    },
                ],
            },
            options: finalChartOptions,
        });

        this.initChartSource(data);
        this.initChartDownloadCsv();
    }

    render() {
        this.shadowRoot.innerHTML = `
            <style>
                .exp-card-chart__body {
                    position: relative;
                    margin-top: 16px;
                    margin-bottom: 24px;
                    height: 500px;
                }
                .chart-error {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    height: 100%;
                    font-family: 'PP Object Sans', sans-serif;
                    color: #595959;
                }
            </style>
            <div id="shortcode-chart-container" class="exp-card-chart__body">Aan het laden...</div>
        `;
    }

    disconnectedCallback() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}

customElements.define('shortcode-chart-component', DefaultChartComponent);
